import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchCourses, submitDemoSignup, api } from "@/lib/api";
import { COMPANY } from "@/lib/company";

/**
 * <QuickDemoDialog course={optionalCourseTitle}>
 *   <button>Contact for demo/pricing</button>
 * </QuickDemoDialog>
 *
 * A single reusable dialog that:
 *  - Pre-selects the course when provided
 *  - Loads upcoming demo dates from GET /api/demo-dates
 *  - Submits to POST /api/demo-signup which emails infohimaniconsult@gmail.com
 */
export default function QuickDemoDialog({ course = "", children, testIdPrefix = "quick-demo" }) {
  const [open, setOpen] = useState(false);
  const [dates, setDates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    course_interest: course,
    preferred_time: "",
    demo_date: "",
    message: "",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/demo-dates").then((r) => setDates(r.data)).catch(() => setDates([]));
    fetchCourses().then(setCourses).catch(() => setCourses([]));
  }, [open]);

  useEffect(() => {
    if (course) setForm((f) => ({ ...f, course_interest: course }));
  }, [course]);

  useEffect(() => {
    if (!open) {
      // reset when closed
      setTimeout(() => {
        setSubmitted(false);
        setForm({
          full_name: "",
          email: "",
          phone: "",
          course_interest: course,
          preferred_time: "",
          demo_date: "",
          message: "",
        });
      }, 200);
    }
  }, [open, course]);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target?.value ?? v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.course_interest) {
      toast.error("Please fill your name, email, phone and course.");
      return;
    }
    setLoading(true);
    const chosen = dates.find((d) => d.date === form.demo_date);
    const payload = {
      ...form,
      preferred_time: form.preferred_time || (chosen ? `${chosen.label} · ${chosen.time_ist}` : "Flexible — please suggest"),
      demo_date: chosen ? `${chosen.label} (${chosen.time_ist})` : form.demo_date || undefined,
      message: form.message || `Interested in demo & pricing for ${form.course_interest}.`,
    };
    try {
      await submitDemoSignup(payload);
      setSubmitted(true);
      toast.success("Sent! The HCS team will email/call you shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        data-testid={`${testIdPrefix}-dialog`}
        className="sm:max-w-[560px] p-0 border border-slate-200 bg-white rounded-none"
      >
        <div className="p-6 md:p-8">
          <DialogHeader>
            <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Contact for demo & pricing</div>
            <DialogTitle className="font-display font-bold tracking-tight text-2xl md:text-3xl text-slate-950 mt-2">
              {course ? course : "Talk to us about your course"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 mt-1">
              We&apos;ll email {COMPANY.email} with your request and follow up within 24 hours.
              Course fees start from INR 19,999 + GST (India) or USD 499 (Global).
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div data-testid={`${testIdPrefix}-success`} className="mt-8 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-[#0052FF]" />
              <div className="mt-4 font-display font-bold text-xl text-slate-950">Request sent!</div>
              <p className="mt-2 text-sm text-slate-600">
                We&apos;ve emailed the HCS team. Expect a call at <span className="font-medium text-slate-900">{form.phone}</span> within 24 hours.
              </p>
              <button
                onClick={() => setOpen(false)}
                className="mt-6 bg-slate-900 text-white text-sm font-medium px-5 py-2.5 hover:bg-slate-700 transition-colors"
                data-testid={`${testIdPrefix}-close-btn`}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="qd_name">Name*</Label>
                  <Input id="qd_name" data-testid={`${testIdPrefix}-name`} required value={form.full_name} onChange={update("full_name")} className="mt-1.5 rounded-sm" placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="qd_email">Email*</Label>
                  <Input id="qd_email" data-testid={`${testIdPrefix}-email`} type="email" required value={form.email} onChange={update("email")} className="mt-1.5 rounded-sm" placeholder="you@example.com" />
                </div>
                <div>
                  <Label htmlFor="qd_phone">Phone*</Label>
                  <Input id="qd_phone" data-testid={`${testIdPrefix}-phone`} required value={form.phone} onChange={update("phone")} className="mt-1.5 rounded-sm" placeholder="+91 98xxxxxxxx" />
                </div>
                <div>
                  <Label>Course*</Label>
                  <Select value={form.course_interest} onValueChange={update("course_interest")}>
                    <SelectTrigger data-testid={`${testIdPrefix}-course`} className="mt-1.5 rounded-sm">
                      <SelectValue placeholder="Pick a course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map((c) => (
                        <SelectItem key={c.id} value={c.title}>{c.title} ({c.vendor})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Pick a demo date</Label>
                <p className="text-xs text-slate-500 mb-2">Upcoming demo slots — pick one or leave blank and we'll suggest.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[180px] overflow-y-auto border border-slate-200 p-2">
                  {dates.length === 0 && <div className="text-sm text-slate-500 p-2">Loading dates…</div>}
                  {dates.map((d) => {
                    const selected = form.demo_date === d.date;
                    return (
                      <button
                        type="button"
                        key={d.date}
                        onClick={() => update("demo_date")(d.date)}
                        data-testid={`${testIdPrefix}-date-${d.date}`}
                        className={`text-left px-3 py-2 text-sm border transition-all ${
                          selected
                            ? "border-[#0052FF] bg-[#0052FF]/5 text-slate-950"
                            : "border-slate-200 hover:border-slate-400 text-slate-700"
                        }`}
                      >
                        <div className="font-medium">{d.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{d.time_ist} · {d.slot}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="qd_msg">Message (optional)</Label>
                <Textarea id="qd_msg" data-testid={`${testIdPrefix}-message`} rows={2} value={form.message} onChange={update("message")} className="mt-1.5 rounded-sm" placeholder="Any specific pricing/schedule questions?" />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid={`${testIdPrefix}-submit-btn`}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] disabled:opacity-70 text-white font-medium px-6 py-3 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Sending…" : "Send request & email the team"}
              </button>
              <p className="text-[11px] text-slate-500 text-center">Delivered to {COMPANY.email}</p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
